import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import moment from 'moment';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import GamesStore from 'stores/GamesStore';
import PaymentStore from 'stores/PaymentStore';
import BalanceInfoMobile from 'components/BalanceInfoMobile/BalanceInfoMobile';
import Button from 'components/Button/Button';
import ErrorAlert from 'components/ErrorAlert/ErrorAlert';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import { GAMES } from 'utils/consts';
import ThemeService from 'themes/ThemeService';

import iconTokens from '../../../images/icontokens.png';

import styles from './SpinTheWheel.css';


const RESULT_DELAY = 4000;
const CLOSE_DELAY = 6000;
const TIMER_DELAY = 1000;
const RADIAN = 360;

function SpinTheWheel(props) {
  const {
    roomName,
    showBuyTokensModal,
    toggleGameDrawer,
    user,
    isDesktop,
    embedProfileV2,
    setShowRegistrationModal,
  } = props;
  const {
    spinTheWheelRoll,
    activeGamesInfo,
    pauseTime,
    prizeWinner,
    result,
    status,
    prizes,
    price,
    clearResult,
    isPlayActionLoading,
  } = GamesStore;
  const { apiToken } = FirebaseStore;
  const { balance } = PaymentStore;
  const items = [];
  const gameDetails = activeGamesInfo.find((game) => game.name === GAMES.SPIN_THE_WHEEL);
  if (gameDetails) {
    Object.values(gameDetails.details.o).forEach((obj) => items.push(obj.o));
  }
  if (!items.length && prizes) {
    Object.values(prizes).forEach((obj) => items.push(obj.o));
  }
  const isWinner = prizeWinner === user.username;
  const selectedItem = result && isWinner && result - 1;
  const [timer, setTimer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [lockButton, setLockButton] = useState(false);
  const [isRolled, setIsRolled] = useState(false);
  const [showNoFunds, setShowNoFunds] = useState(false);
  const deg = (5 * RADIAN) + ((-RADIAN * selectedItem) / items.length);
  const selectedClass = { transform: `rotate(${deg}deg)` };
  const spinning = selectedItem ? styles.spinning : '';
  const noFunds = parseInt(balance, 10) < parseInt(price, 10) || !user.username;
  const buttonDisabled = Boolean(timer) || lockButton || status !== 'IDLE';
  const showNoFundsOverlay = (noFunds && !isDesktop) || (showNoFunds && noFunds);

  const getDeg = (index) => {
    const degValue = index * (RADIAN / items.length);

    return { transform: `translateY(-50%) rotate(${degValue}deg)` };
  };

  const roll = useCallback(() => {
    if (noFunds) {
      setShowNoFunds(true);
    } else {
      setLockButton(true);
      setIsRolled(true);
      spinTheWheelRoll(roomName, apiToken);
    }
  }, [apiToken, noFunds, roomName, spinTheWheelRoll]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (pauseTime && status === 'PAUSED') {
      const countdown = setInterval(() => {
        const timeLeft = moment(pauseTime).diff(moment());
        const formatted = moment(timeLeft).format('mm:ss');
        if (timeLeft < 0) {
          setTimer(null);
          clearInterval(countdown);
          setLockButton(false);
          clearResult();
        } else {
          setTimer(formatted);
        }
      }, TIMER_DELAY);

      return () => {
        clearInterval(countdown);
      };
    }
    setTimer(null);
    setLockButton(false);

  }, [pauseTime, status, clearResult]);

  useEffect(() => {
    if (apiToken) {
      const data = {
        game: GAMES.SPIN_THE_WHEEL,
        roomName,
        token: apiToken,
      };
      GamesStore.getGameInfo(data);
    }
  }, [roomName, apiToken]);

  const handleDrawer = useCallback(() => {
    if (!isDesktop) {
      toggleGameDrawer();
    } else {
      clearResult();
      ChatStore.setProfileChatTabValue(0);
    }
  }, [isDesktop, toggleGameDrawer, clearResult]);

  useEffect(() => {
    let resultTimeoutId = null;
    let closeTimeoutId = null;
    if (selectedItem && isRolled) {
      resultTimeoutId = setTimeout(() => setShowResult(true), RESULT_DELAY);
      closeTimeoutId = setTimeout(() => {
        setShowResult(false);
        setIsRolled(false);
        handleDrawer();
      }, CLOSE_DELAY);
    }

    return () => {
      clearTimeout(resultTimeoutId);
      clearTimeout(closeTimeoutId);
    };
  }, [selectedItem, handleDrawer, isRolled]);

  const onShowBuyTokensModal = useCallback(() => {
    if (embedProfileV2 && !user.username) {
      setShowRegistrationModal(true);
    } else {
      showBuyTokensModal();
    }
  }, [embedProfileV2, showBuyTokensModal, setShowRegistrationModal, user.username]);

  const onOpenBuyTokensModal = useCallback(() => {
    handleDrawer();
    onShowBuyTokensModal();
  }, [handleDrawer, onShowBuyTokensModal]);

  const wheelTheme = ThemeService.theme.games.wheel;

  const containerClasses = classNames(styles.container, wheelTheme.containerClass,
    { [styles.containerDesktop]: isDesktop }
  );

  const wheelContainerClasses = classNames(styles.wheelContainer,
    { [styles.wheelContainerDesktop]: isDesktop }
  );

  const spinBtnClasses = classNames(styles.spinBtn,
    { [styles.spinBtnDesktop]: isDesktop }
  );

  const outerCircleClasses = classNames(styles.outerCircle, wheelTheme.outerCircleClass);

  const arrowClasses = classNames(styles.arrow, wheelTheme.arrowClass);

  const centerClasses = classNames(styles.center, wheelTheme.centerClass);

  const balanceInfoElement = useMemo(() => {
    if (isDesktop) {
      return null;
    }

    return (
      <BalanceInfoMobile
        areTokensNeeded={balance < price}
        id="spinTheWheel_buyTokens_mobile"
        onClick={onOpenBuyTokensModal}
      />
    );
  }, [
    balance,
    onOpenBuyTokensModal,
    isDesktop,
    price,
  ]);

  return (
    <Fragment>
      <div className={containerClasses}>
        <div className={wheelContainerClasses}>
          <div className={outerCircleClasses} />
          <div className={arrowClasses} />
          <div className={centerClasses} />
          <div className={`${styles.wheel} ${spinning}`} style={spinning ? selectedClass : null}>
            {items.map((item, index) => {
              let colorClass = styles[`color${index}`];
              if (items.length === 7 && index === 6) {
                colorClass = styles.color1;
              }

              return (
              // eslint-disable-next-line react/no-array-index-key -- necessary evil
                <div
                  key={index}
                  className={`${styles[`wheelItem${items.length}`]} ${colorClass}`}
                  style={getDeg(index)
                  }
                >
                  {item}
                </div>
              );
            })}
          </div>
        </div>
        {!timer ? (
          <div className={styles.price}>
            <img alt="price" src={iconTokens} />
            {price}
          </div>
        ) : (
          <div className={styles.timer}>{timer}</div>
        )}
        <LoadingButton
          className={spinBtnClasses}
          colour="orange"
          disabled={buttonDisabled}
          id={`spinTheWheelButton_${isDesktop ? 'desktop' : 'mobile'}`}
          isLoading={isPlayActionLoading}
          onClick={roll}
        >
          {translate('tipping.tipping.games.game.wheel.ui.spin.text')}
        </LoadingButton>
        {showResult && (
          <div className={styles.result}>
            <span>{items[selectedItem]}</span>
          </div>
        )}
        {(showNoFundsOverlay && isDesktop) && (
          <div className={styles.noFunds}>
            <ErrorAlert message={translate('v2profile.insufficient.balance')} />
            <Button
              colour="green"
              id="spinTheWheel_buyTokens_desktop"
              label={translate('v2profile.buy.tokens')}
              onClick={onOpenBuyTokensModal}
            />
          </div>
        )}
      </div>
      {balanceInfoElement}
    </Fragment>
  );
}

SpinTheWheel.propTypes = {
  roomName: PropTypes.string.isRequired,
  showBuyTokensModal: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  embedProfileV2: PropTypes.bool,
  isDesktop: PropTypes.bool,
  // Only used when isDesktop === false
  setShowRegistrationModal: PropTypes.func,
  toggleGameDrawer: PropTypes.func,
};
SpinTheWheel.defaultProps = {
  embedProfileV2: false,
  isDesktop: false,
  setShowRegistrationModal: () => undefined,
  toggleGameDrawer: () => undefined,
};

export default observer(SpinTheWheel);
